import { useEffect, useMemo, useRef, useState } from 'react'
import { Stethoscope, Link2, Pencil } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { buildDocInfo } from '../../lib/doctorFilter.js'
import { apptStatusInfo } from '../../data/appointmentStatuses.js'
import { labDayKey, labDatesForWrite, isInHouseBench, hasRetainer, retainerSummary } from '../../lib/lab.js'
import {
  Field, SearchSelect, BranchSelect, Checkbox, Textarea, Badge, Banner, InlineError, DrawerFooter, FIELD_CLASS,
  fmtDate, fmtDateTime, branchName as kitBranchName,
} from '../../kit'
import LinkPicker from './LinkPicker.jsx'
import LabDayPicker from './LabDayPicker.jsx'
import RetainerBuilder from './RetainerBuilder.jsx'

// The ONE lab form. `mode="create"` renders it inside LabCreateDrawer with its own
// ยกเลิก/บันทึก; `mode="edit"` renders the same fields inside LabDetailDrawer's ข้อมูล Lab
// section as part of that drawer's single edit mode — **no buttons of its own**, reporting
// its fields upward through `onFieldsChange` so the drawer's one บันทึก can send them with
// everything else in one write. Add and edit are the same act with a different starting
// row, so they are deliberately not two forms that drift apart.
//
// Fields: การรักษา · นัดหมาย · ผู้ผลิต Lab · งานแก้ · วันที่ · สาขา · หมอ (+ รายละเอียดงาน on create).
//
// ⚠️ FOUR UPSTREAM RULES THIS FORM IS SHAPED BY (POST /lab, api 194 — the backup in
// knowledge/xano/backups/lab_POST_api194_2026-08-17_REFERENCE.xs spells them out):
//
//  1. **การรักษา is required, and that is not a policy choice.** GET /lab RIGHT-joins
//     treatment, so a lab with treatment_id = 0 is written successfully and then never
//     returned by the API again — invisible on this page, on /lab, everywhere. 8,399 of
//     38,742 live rows are already in that state.
//  2. **A link can be changed but never removed.** Every field falls back to the
//     existing row via first_notempty, so sending 0 keeps the old value. There is no
//     "— ไม่ผูก —" option once a link exists, because the button would silently do
//     nothing. The form says so rather than lying.
//  3. **Upstream rewrites `lab.date` from the appointment, and that IS the rule** — the
//     form no longer works around it. A lab carries two dates (Ram, 2026-09-07):
//     **กำหนดส่ง (`deadline`) is the one a human picks** — when the piece must be at the
//     clinic, or, for the in-house bench, the day its daily quota is counted on — while
//     **วันที่ (`date`) is the handover day and simply follows the linked นัดหมาย.** So
//     the calendar below drives `deadline`, and วันที่ is shown read-only when a link
//     exists. With no link there is no handover day to follow, and `date` takes the
//     deadline's value so the row is never undated. The whole mapping is one pure
//     function, `lib/lab.js#labDatesForWrite`.
//  4. **`deadline` is preserved by omission upstream**, so it is sent on every write that
//     sets a date rather than only when it changes — it is the field being edited now, not
//     a mirror of another one.
//
// สาขา is used ONLY to filter the doctor list (the lab table has no branch column — the
// /lab board derives branch from the treatment join), so it is never saved.
const BKK = 7 * 3600 * 1000
const pad = (n) => String(n).padStart(2, '0')
const asList = (r) => (Array.isArray(r) ? r : (r?.items || []))
function msToISO(ms) { const n = Number(ms); if (!n) return ''; const d = new Date(n + BKK); return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` }
function isoToMs(iso) { if (!/^\d{4}-\d{2}-\d{2}/.test(iso || '')) return 0; const [y, m, d] = iso.slice(0, 10).split('-').map(Number); return Date.UTC(y, m - 1, d) - BKK }
// A doctor is "on shift" for the day/branch when a timetable row is a shift block.
function extractDoctors(rows) {
  const m = new Map()
  for (const a of rows) {
    if (a.event_type !== 'inverse-background' || a.block === true) continue
    const did = Number(a.doctor_id) || 0
    if (!did) continue
    if (!m.has(did)) m.set(did, { id: String(did), code: a.doctor?.code || a.titleDoctorTB || `หมอ #${did}`, img: a.doctor?.profile_img || '' })
    else if (!m.get(did).img && a.doctor?.profile_img) m.get(did).img = a.doctor.profile_img
  }
  return [...m.values()].sort((x, y) => x.code.localeCompare(y.code, 'th'))
}
const firstLine = (s) => String(s || '').split('\n').map((x) => x.trim()).find(Boolean) || ''

export default function LabInfoEditor({ row = {}, patientId, patientName = '', patientHN = '', mode = 'edit', onSave, onCancel, onFieldsChange }) {
  const creating = mode === 'create'
  const pid = Number(patientId ?? row.patient_id) || 0

  const [treatmentId, setTreatmentId] = useState(String(row.treatment_id || ''))
  const [apptId, setApptId] = useState(String(row.appointment_id || ''))
  const [lablistId, setLablistId] = useState(String(row.lablist_id || ''))
  // กำหนดส่ง — what the calendar picks. Falls back to `date` on a legacy row that has no
  // deadline (0 on the old imports), so opening one shows the day it actually holds.
  const [deadline, setDeadline] = useState(() => msToISO(Number(row.deadline) || Number(row.date)))
  const [branchId, setBranchId] = useState(String(row.branch_id || ''))
  const [doctorId, setDoctorId] = useState(String(row.doctor_id || ''))
  const [repair, setRepair] = useState(!!row.repair)
  const [detail, setDetail] = useState(row.detail || '')
  // CREATE-only: the retainer spec, built here and sent with the insert instead of in a
  // second write. null until the counter opens the builder. Edit mode never uses this —
  // LabDetailDrawer already owns that entry point, and two buttons for one act is worse
  // than one. See `showRetainer` below.
  const [spec, setSpec] = useState(null)      // {production_type, retainer} | null
  const [specOpen, setSpecOpen] = useState(false)

  const [vendors, setVendors] = useState([])
  const [branches, setBranches] = useState([])
  const [treatments, setTreatments] = useState(null)   // null = loading
  const [appts, setAppts] = useState(null)
  const [apptTypes, setApptTypes] = useState({})
  const [allDoctors, setAllDoctors] = useState([])
  const [tmDoctors, setTmDoctors] = useState([])
  const [tmLoading, setTmLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  // Links present on the row when the form opened. Rule 2 above keys off THIS, not off
  // live state: a link the user has just chosen is still removable (they can pick a
  // different one) — a link already persisted upstream is not.
  const hadTreatment = Number(row.treatment_id) > 0
  const hadAppt = Number(row.appointment_id) > 0

  useEffect(() => {
    cachedGet('/api/lab/lablist').then((r) => setVendors(asList(r))).catch(() => {})
    cachedGet('/api/branches').then((r) => setBranches(asList(r).map((b) => ({ id: String(b.id ?? b.branch_id), name: b.name || b.branch_name || `#${b.id}` })).sort((a, b) => Number(a.id) - Number(b.id)))).catch(() => {})
    cachedGet('/api/doctors').then((r) => setAllDoctors(asList(r))).catch(() => {})
    cachedGet('/api/appointment-types').then((r) => {
      const m = {}
      for (const t of asList(r)) m[String(t.id)] = [t.type, t.detail].filter(Boolean).join(' · ')
      setApptTypes(m)
    }).catch(() => {})
  }, [])

  // This patient's visits and appointments — the two link pick-lists. Both are already
  // patient-scoped server-side, so nothing is filtered client-side here. An empty array
  // (not null) on failure: the form must still be usable to fix a vendor.
  useEffect(() => {
    if (!pid) { setTreatments([]); setAppts([]); return }
    let alive = true
    apiGet('/api/treatments', { patient_id: pid, per_page: 200 })
      .then((r) => { if (alive) setTreatments(asList(r)) }).catch(() => { if (alive) setTreatments([]) })
    apiGet('/api/appointments', { patient_id: pid })
      .then((r) => { if (alive) setAppts(asList(r)) }).catch(() => { if (alive) setAppts([]) })
    return () => { alive = false }
  }, [pid])

  // id → display name; the kit reads a head-office (สนามเป้า) row as อารีย์.
  const branchName = useMemo(
    () => Object.fromEntries(branches.map((b) => [b.id, kitBranchName(b.id, branches)])), [branches])
  const docInfo = useMemo(() => buildDocInfo(allDoctors), [allDoctors])

  // Everything the picker rows render. A visit is NOT identified by its date — a patient
  // can have three in one day — so each option carries the doctor, the branch, what was
  // done and how many billing lines it holds. `/treatment?type=patient` already nests all
  // of it (doctor + appointmentlist addons, billing[] via addon 21), so none of this
  // costs an extra call.
  const treatmentOpts = useMemo(() => (treatments || [])
    .map((t) => ({
      id: String(t.id),
      date: t.date,
      doctor_id: t.doctor_id,
      branch_id: t.branch_id,
      detail: firstLine(t.detail),
      remark: firstLine(t.remark),
      docCode: t.doctor?.code || t.doctor?.name || '',
      docImg: t.doctor?.profile_img || '',
      type: (t.appointmentlist || t.appointmentList || {}).type || '',
      lines: Array.isArray(t.billing) ? t.billing.length : 0,
    }))
    .sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))), [treatments])

  const apptOpts = useMemo(() => (appts || [])
    .map((a) => ({
      id: String(a.id), start: Number(a.start_date) || 0, doctor_id: a.doctor_id, branch_id: a.branch_id,
      duration: Number(a.duration) || 0,
      status: a.status || '',
      remark: firstLine(a.remark),
      label: apptTypes[String(a.appointmentlist_id)] || firstLine(a.details) || a.title || '',
    }))
    .sort((a, b) => b.start - a.start), [appts, apptTypes])

  // The two row renderers. Same shell (LinkPicker), deliberately different content:
  // a visit is read date-first, an appointment is read time-first.
  const renderTreatment = (t) => (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap text-[12.5px]">
        <span className="font-semibold text-slate-800 tabular-nums">{fmtDate(t.date)}</span>
        {branchName[String(t.branch_id)] && <><Dot /><span className="text-slate-500">{branchName[String(t.branch_id)]}</span></>}
        {(t.docCode || docInfo[String(t.doctor_id)]?.code) && (
          <><Dot /><Doc img={t.docImg || docInfo[String(t.doctor_id)]?.img} code={t.docCode || docInfo[String(t.doctor_id)]?.code} /></>
        )}
        {t.type && <Badge tone="brand">{t.type}</Badge>}
        {t.lines > 0 && <Badge>{t.lines} รายการ</Badge>}
      </div>
      {(t.detail || t.remark) && (
        <div className="mt-0.5 text-[11.5px] text-slate-500 truncate" title={[t.detail, t.remark].filter(Boolean).join(' · ')}>
          {t.detail}{t.detail && t.remark && <span className="text-slate-300"> · </span>}
          {t.remark && <span className="text-slate-400">{t.remark}</span>}
        </div>
      )}
    </div>
  )

  const renderAppt = (a) => {
    const st = a.status ? apptStatusInfo(a.status) : null
    const doc = docInfo[String(a.doctor_id)]
    return (
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[12.5px]">
          <span className="font-semibold text-slate-800 tabular-nums">{fmtDateTime(a.start)}</span>
          {a.duration > 0 && <><Dot /><span className="text-slate-500 tabular-nums">{a.duration} นาที</span></>}
          {st?.label && <Badge>{st.label}</Badge>}
          {/* Only ever an already-saved link — the list never offers one of these. */}
          {a.outOfRange && <Badge tone="rose">ก่อนวันรักษา</Badge>}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap text-[11.5px] text-slate-500 min-w-0">
          {a.label && <span className="truncate max-w-[190px]">{a.label}</span>}
          {doc?.code && <><Dot /><Doc img={doc.img} code={doc.code} /></>}
          {branchName[String(a.branch_id)] && <><Dot /><span>{branchName[String(a.branch_id)]}</span></>}
        </div>
        {a.remark && <div className="mt-0.5 text-[11.5px] text-slate-400 truncate" title={a.remark}>{a.remark}</div>}
      </div>
    )
  }

  // What the search box matches on. Everything visible on the row, so typing a doctor
  // code, a branch or a word from the note all narrow the list.
  const treatmentSearch = (t) => [fmtDate(t.date), t.date, branchName[String(t.branch_id)], t.docCode, t.type, t.detail, t.remark].filter(Boolean).join(' ')
  const apptSearch = (a) => [fmtDateTime(a.start), a.label, docInfo[String(a.doctor_id)]?.code, branchName[String(a.branch_id)], a.remark].filter(Boolean).join(' ')

  // Is this piece made on our own bench? Decides whether the add form asks for the
  // retainer spec — see the ประเภทงาน & รีเทนเนอร์ block in the markup.
  const inHouse = isInHouseBench(lablistId)

  const vendorName = vendors.find((v) => String(v.id) === String(lablistId))?.name || ''

  // ผู้ผลิต Lab is a kit SearchSelect, not a native <select> (Ram, 2026-09-07).
  // The vendor list is long enough that finding one meant scrolling a native dropdown
  // reading Thai names in file order; typing three characters is the whole point.
  const vendorOpts = useMemo(() => vendors.map((v) => ({
    value: String(v.id),
    // in-house stays IN the label, not in `hint`: it is the one thing that changes what
    // the form then asks for (the retainer spec block), so it must survive the closed
    // trigger, which shows the label alone.
    label: `${v.name || `Lab #${v.id}`}${v.inhouse ? ' (in-house)' : ''}`,
  })), [vendors])

  const chosenTreatment = treatmentOpts.find((t) => t.id === String(treatmentId)) || null

  // ── นัดหมาย is downstream of การรักษา (Ram, 2026-08-17) ───────────────────────
  // A lab is ordered AT a visit and delivered at a later appointment, so an
  // appointment that predates its own treatment is a mislink, not a valid pairing.
  // Two rules follow, and they are separate:
  //   1. no treatment → the picker is disabled outright; there is no floor to filter
  //      against and nothing to attach the appointment to.
  //   2. treatment chosen → only appointments at or after Bangkok MIDNIGHT of the visit
  //      day are offered. Midnight, not the visit's own timestamp, so the appointment
  //      booked earlier the same morning still counts — same-day is the common case.
  const apptFloor = chosenTreatment ? isoToMs(String(chosenTreatment.date || '').slice(0, 10)) : 0
  const apptInRange = useMemo(
    () => (apptFloor ? apptOpts.filter((a) => a.start >= apptFloor) : apptOpts),
    [apptOpts, apptFloor])
  const hiddenApptCount = apptOpts.length - apptInRange.length

  // The list the picker actually gets. An ALREADY-LINKED appointment that the floor
  // excludes is kept in, flagged — dropping it would leave the picker showing
  // "ไม่ผูกนัดหมาย" while `appointment_id` was still set upstream, which is a lie the
  // form cannot back out of (first_notempty means it cannot be cleared).
  const apptItems = useMemo(() => {
    if (!apptId || apptInRange.some((a) => a.id === String(apptId))) return apptInRange
    const cur = apptOpts.find((a) => a.id === String(apptId))
    return cur ? [{ ...cur, outOfRange: true }, ...apptInRange] : apptInRange
  }, [apptInRange, apptOpts, apptId])

  const chosenAppt = apptItems.find((a) => a.id === String(apptId)) || null
  // TRUE only while the appointment link is being MOVED in this very save — that is the
  // one case upstream overrules the date field. A link that is merely present does not.
  const apptChanged = Number(apptId || 0) !== Number(row.appointment_id || 0)

  // ── กำหนดส่ง may not run past the appointment (Ram, 2026-08-18, restated 2026-09-07) ──
  // The appointment is when the piece is handed over, so a deadline after it means the
  // work is due after it was delivered. Inclusive: same-day is the normal case, and
  // EARLIER is the deliberate one — a piece the clinic wants in hand before the visit.
  // Enforced by DISABLING later days in the picker, so a new violation is unreachable; a
  // legacy row already past its appointment is warned about rather than blocked, since
  // refusing the save would strand the record.
  const apptDay = chosenAppt?.start ? labDayKey(chosenAppt.start) : ''
  const deadlineAfterAppt = !!apptDay && !!deadline && deadline > apptDay
  // The day the DOCTOR is read off: the visit, when there is one. A piece due early is
  // still fitted by whoever is on shift at the appointment, not by whoever works the day
  // the lab arrives — and before the deadline/date split this was the same value.
  const effectiveIso = apptDay || deadline

  // Doctors on shift for the effective date + branch. The row's CURRENT doctor is
  // merged in even when they were not rostered that day — otherwise reopening an old
  // lab shows an empty หมอ box for a doctor who is very much set on the record.
  useEffect(() => {
    if (!effectiveIso || !branchId) { setTmDoctors([]); return }
    let alive = true; setTmLoading(true)
    apiGet('/api/appointments', { type: 'timetable', branch_id: branchId, start_date: effectiveIso, end_date: effectiveIso })
      .then((r) => { if (alive) setTmDoctors(extractDoctors(asList(r))) })
      .catch(() => { if (alive) setTmDoctors([]) })
      .finally(() => { if (alive) setTmLoading(false) })
    return () => { alive = false }
  }, [effectiveIso, branchId])

  const doctorOpts = useMemo(() => {
    const list = [...tmDoctors]
    if (doctorId && !list.some((d) => d.id === String(doctorId))) {
      const d = allDoctors.find((x) => String(x.id) === String(doctorId))
      list.unshift({
        id: String(doctorId),
        code: d ? (d.code || [d.name, d.surname].filter(Boolean).join(' ')) : `หมอ #${doctorId}`,
        img: d?.profile_img || '',
        offShift: true,
      })
    }
    return list
  }, [tmDoctors, doctorId, allDoctors])
  const doctorPickOpts = useMemo(() => doctorOpts.map((d) => ({
    value: d.id, label: d.code, hint: d.offShift ? 'ไม่ได้ออกตรวจในวัน/สาขานี้' : undefined,
  })), [doctorOpts])

  // Picking a visit fills in what that visit already knows. The lab belongs to it, so
  // its date / branch / doctor are the right defaults — all three stay editable after.
  //
  // It also re-checks the appointment, because moving the treatment moves the floor.
  // An appointment the new visit invalidates is DROPPED only when nothing is persisted
  // yet (`!hadAppt`) — clearing a saved link would show "ไม่ผูกนัดหมาย" for a link the
  // save could not actually remove. When it is persisted, it stays and gets flagged.
  function chooseTreatment(id) {
    setTreatmentId(id)
    const t = treatmentOpts.find((x) => x.id === String(id))
    // Cleared the visit → the appointment loses both its floor and its anchor.
    if (!t) { if (!hadAppt) setApptId(''); return }
    if (t.branch_id) setBranchId(String(t.branch_id))
    if (t.doctor_id) setDoctorId(String(t.doctor_id))

    const floor = isoToMs(String(t.date || '').slice(0, 10))
    const cur = apptOpts.find((x) => x.id === String(apptId))
    const apptStillValid = !!cur && (!floor || cur.start >= floor)
    if (!apptStillValid && !hadAppt && apptId) setApptId('')
    // The visit's own date seeds กำหนดส่ง whenever no appointment is left to own it.
    // No cap needed here: this branch runs only when the appointment is absent or has just
    // been invalidated, so there is no appointment day to run past. When one IS kept, the
    // deadline is left alone — and one that satisfied the cap before still does.
    if ((!apptStillValid || !apptId) && t.date) setDeadline(String(t.date).slice(0, 10))
  }
  function chooseAppt(id) {
    setApptId(id)
    const a = apptOpts.find((x) => x.id === String(id))
    if (!a) return
    if (a.branch_id) setBranchId(String(a.branch_id))
    // The new appointment's day seeds กำหนดส่ง: same-day is the usual case, and it can
    // never violate the cap it defines. Moving it EARLIER afterwards is the whole point of
    // the field, so this is a default, not a lock — `date` is what follows the link.
    if (a.start) setDeadline(msToISO(a.start))
    if (a.doctor_id) setDoctorId(String(a.doctor_id))
  }

  // A dated row is not optional on create: lib/lab.js#labCardState gives an undated lab
  // no card at all, and the ledger files it under "ไม่ระบุปี" — 125 live rows are already
  // in that hole. Every treatment carries a date, so this only fires on a stray one.
  //
  // On EDIT the same date is required for a different reason: Xano validates `month`
  // as 1–12, so a lab with no resolvable month cannot be written at all (see RULE 4).
  // Better to disable บันทึก and say so than to let the user meet ERROR_CODE_INPUT_ERROR.
  const monthResolvable = !!deadline || !!apptDay || Number(row.date) > 0 || Number(row.month) > 0
  const canSave = !!lablistId
    && (!creating || (!!treatmentId && !!deadline))
    && (creating || monthResolvable)
  // Why เพิ่มงาน Lab is off (display only — canSave above is the rule).
  const blockReason = !lablistId ? 'เลือกผู้ผลิต Lab ก่อน'
    : creating && !treatmentId ? 'เลือกการรักษาก่อน'
      : creating && !deadline ? 'เลือกกำหนดส่งก่อน'
        : !creating && !monthResolvable ? 'ต้องระบุวันที่ก่อนบันทึก' : ''

  // What this form would write, derived rather than assembled inside a click handler —
  // embedded mode has no click handler of its own to assemble it in.
  const fields = useMemo(() => {
    const f = { lablist_id: Number(lablistId) || 0, repair }

    // The picker speaks DAYS; the record remembers a timestamp. When the chosen day is the
    // one the row already holds, resend the stored ms rather than the day's midnight —
    // otherwise every unrelated save quietly resets a 12:30 piece to 00:00, a drift
    // nothing on this page displays and so nobody would catch for months.
    const storedDl = Number(row.deadline) || 0
    const storedDate = Number(row.date) || 0
    const deadlineMs =
      (storedDl && msToISO(storedDl) === deadline) ? storedDl
      : (!storedDl && storedDate && msToISO(storedDate) === deadline) ? storedDate
      : isoToMs(deadline)

    // กำหนดส่ง is the choice; วันที่ follows the link. Rule 3 in the header.
    const d = labDatesForWrite({
      deadlineMs,
      // ONLY a moving link hands วันที่ over — upstream rewrites it from the appointment
      // on any write carrying appointment_id, and that is exactly what we want it to do.
      apptMovingToMs: apptChanged && apptId ? (chosenAppt?.start || 0) : 0,
      storedDateMs: creating ? 0 : storedDate,
      linked: !!apptId,
    })

    if (creating) {
      f.patient_id = pid
      f.treatment_id = Number(treatmentId) || 0
      if (apptId) f.appointment_id = Number(apptId)
      f.detail = detail
      if (spec) { f.production_type = spec.production_type; f.retainer = spec.retainer }
    } else {
      if (Number(treatmentId || 0) !== Number(row.treatment_id || 0) && treatmentId) {
        f.treatment_id = Number(treatmentId)
      }
      // Sent only when the link actually moved: on any other edit it would re-trigger the
      // upstream rewrite and drag วันที่ onto the appointment of a legacy row that had
      // deliberately been left disagreeing with it.
      if (apptChanged && apptId) f.appointment_id = Number(apptId)
    }
    f.doctor_id = Number(doctorId) || 0

    // ── RULE 5, learned the hard way (2026-08-17) ────────────────────────────────
    // `lab.month` carries min:1/max:12 validators and the dblink input is NOT nullable,
    // so an ABSENT month reaches Xano as 0 and the entire write 400s with
    // ERROR_CODE_INPUT_ERROR. date/year/month therefore ride along on EVERY write, not
    // only when something moved — which is what the first version of this did, so any
    // edit to a lab whose appointment was unchanged was rejected outright. The stored
    // columns are the fallback for an undated legacy row; `monthResolvable` refuses the
    // save when even that comes up empty, rather than letting the user meet a raw 400.
    f.date = d.date
    f.deadline = d.deadline
    f.year = d.year || Number(row.year) || 0
    f.month = d.month || Number(row.month) || 0
    return f
  }, [creating, lablistId, repair, chosenAppt, apptChanged, deadline, apptId, treatmentId, doctorId, detail, spec, pid, row])

  // Embedded: hand the fields (and whether they are usable) to the drawer on every
  // change. JSON-compared so an unchanged object does not re-notify the parent forever.
  const lastSent = useRef('')
  useEffect(() => {
    if (!onFieldsChange) return
    const sig = JSON.stringify([fields, canSave])
    if (sig === lastSent.current) return
    lastSent.current = sig
    onFieldsChange(fields, canSave)
  }, [fields, canSave, onFieldsChange])

  async function save() {
    setBusy(true); setErr('')
    try { await onSave(creating ? null : row, fields) } catch (e) { setErr(e?.body?.detail || e?.message || 'บันทึกไม่สำเร็จ'); setBusy(false) }
  }

  return (
    <div className="space-y-3">
      {/* ── เชื่อมโยง — its own section, above the lab's own fields ──────────────
          These two answer "which piece of work is this for", which is a different
          question from "who makes it and when". Grouping them stops the required
          การรักษา from reading as one more attribute of the lab order. */}
      <section className="rounded-lg bg-slate-50 border border-slate-200/70 p-3 space-y-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">เชื่อมโยง</div>

        <LinkPicker
          label="การรักษา"
          required={creating}
          items={treatmentOpts}
          value={treatmentId}
          onChange={chooseTreatment}
          loading={treatments === null}
          disabled={busy}
          allowClear={!hadTreatment}
          placeholder="เลือกการรักษา"
          emptyText="คนไข้รายนี้ยังไม่มีประวัติการรักษา"
          clearText="ยังไม่เลือก"
          searchOf={treatmentSearch}
          renderRow={renderTreatment}
        />
        {/* Not a nag — it is the difference between a record that exists and one that is
            written and then unreachable. See rule 1 in the header. */}
        {creating && !treatmentId && (
          <Banner tone="warn">งาน Lab ต้องผูกกับการรักษา ไม่งั้นจะไม่แสดงในระบบ</Banner>
        )}

        <LinkPicker
          label="นัดหมาย"
          items={apptItems}
          value={apptId}
          onChange={chooseAppt}
          loading={appts === null}
          disabled={busy || !treatmentId}
          disabledText="เลือกการรักษาก่อน"
          allowClear={!hadAppt}
          placeholder="ไม่ผูกนัดหมาย"
          emptyText={apptFloor && apptOpts.length
            ? `ไม่มีนัดหมายตั้งแต่ ${fmtDate(chosenTreatment?.date)} เป็นต้นไป`
            : 'คนไข้รายนี้ยังไม่มีนัดหมาย'}
          clearText="ไม่ผูกนัดหมาย"
          searchOf={apptSearch}
          renderRow={renderAppt}
        />
        {/* Optional on purpose: a piece sometimes has to be sent before the patient knows
            when they are coming back (Ram, 2026-09-07). Recommended, not required — so
            this is a note, not the amber warning การรักษา gets, which blocks a real hole. */}
        {creating && !!treatmentId && !apptId && (
          <Hint icon={Link2}>ยังไม่ผูกนัดหมาย — บันทึกได้ แต่ควรผูกถ้ารู้วันนัดครั้งถัดไป</Hint>
        )}
        {/* Says WHY an appointment they expected is missing. Without it the filter looks
            like a bug — "the นัดหมาย I want isn't in the list". */}
        {!!treatmentId && hiddenApptCount > 0 && (
          <Hint>
            แสดงเฉพาะนัดหมายตั้งแต่ {fmtDate(chosenTreatment?.date)} เป็นต้นไป · ซ่อนไว้ {hiddenApptCount} รายการ
          </Hint>
        )}
        {chosenAppt?.outOfRange && (
          <Banner tone="warn">นัดหมายที่ผูกไว้อยู่ก่อนวันรักษา — เลือกนัดหมายใหม่</Banner>
        )}
        {(hadAppt || hadTreatment) && (
          <Hint icon={Link2}>เปลี่ยนการเชื่อมโยงได้ แต่ยกเลิกไม่ได้</Hint>
        )}
      </section>

      {creating && !!treatmentId && !effectiveIso && (
        <Banner tone="warn">ต้องระบุวันที่ ไม่งั้นงานจะไม่ขึ้นในรายการที่ต้องติดตาม</Banner>
      )}
      {!creating && !monthResolvable && (
        <Banner tone="warn">งาน Lab นี้ไม่มีวันที่ — ต้องระบุวันที่ก่อนจึงจะบันทึกได้</Banner>
      )}

      <Field label="ผู้ผลิต Lab" required>
        <SearchSelect
          options={vendorOpts}
          value={lablistId}
          onChange={(v) => setLablistId(String(v ?? ''))}
          disabled={busy}
          placeholder="เลือก Lab…"
          searchPlaceholder="พิมพ์ชื่อ Lab…"
          emptyText="ไม่พบ Lab ที่ค้นหา"
        />
      </Field>

      <Checkbox checked={repair} onChange={setRepair} disabled={busy} label="งานแก้" />

      {/* กำหนดส่ง breaks out of the two-column Row grid: the calendar needs the full width,
          and the whole point of it is the per-day workload beside each number. It is the
          ONE date a human picks here — and on the in-house bench it is also the day the
          piece counts against, which is why the workload sits under it. */}
      <div className="space-y-1.5">
        <span className="block text-[12.5px] font-medium text-slate-600">
          กำหนดส่ง{creating && <span className="text-rose-500 ml-0.5" aria-hidden>*</span>}{inHouse && <span className="ml-1.5 text-[12px] font-normal text-violet-500">· วันที่นับคิวงานบนเบนช์</span>}
        </span>
        <LabDayPicker
          value={deadline}
          onChange={setDeadline}
          lablistId={lablistId}
          vendorName={vendorName}
          maxDay={apptDay}
          disabled={busy}
        />
        {deadlineAfterAppt && (
          <Banner tone="warn">
            กำหนดส่งอยู่หลังวันนัดหมาย ({fmtDate(apptDay)}) — ควรเลือกวันก่อนหน้า
          </Banner>
        )}
      </div>

      {/* วันที่ is NOT an input any more: it is the handover day and it follows the
          นัดหมาย (upstream rewrites it from the appointment — see rule 3). Shown rather
          than hidden because it is what the /lab board, the ledger and payroll all file
          the piece under, so staff need to see where it landed. */}
      <Field label="วันที่" hint="ตามนัดหมาย หรือกำหนดส่งเมื่อยังไม่ผูกนัด — แก้เองไม่ได้">
        <div className={`${FIELD_CLASS} bg-slate-50 text-slate-500 flex items-center gap-1.5`}>
          {apptId ? (
            <>
              <span className="tabular-nums">{fmtDateTime(chosenAppt?.start || row.date)}</span>
              <span className="text-[12px] text-slate-400">· ตามนัดหมาย</span>
            </>
          ) : (
            <>
              <span className="tabular-nums">{deadline ? fmtDate(deadline) : '—'}</span>
              <span className="text-[12px] text-slate-400">· ตามกำหนดส่ง (ยังไม่ผูกนัดหมาย)</span>
            </>
          )}
        </div>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="สาขา (กรองหมอ)">
          <BranchSelect mode="form" branches={branches} value={branchId}
            onChange={(v) => setBranchId(String(v ?? ''))} disabled={busy} />
        </Field>
        <Field label="หมอ (ออกตรวจวันนี้)">
          <SearchSelect
            options={doctorPickOpts}
            value={doctorId}
            onChange={(v) => setDoctorId(String(v ?? ''))}
            disabled={!effectiveIso || !branchId}
            placeholder={!effectiveIso || !branchId ? 'เลือกวันที่และสาขาก่อน' : tmLoading ? 'กำลังโหลด…' : (doctorPickOpts.length ? 'เลือกหมอ…' : 'ไม่มีหมอออกตรวจ')}
            searchPlaceholder="ค้นหาหมอ…"
            emptyText={tmLoading ? 'กำลังโหลด…' : 'ไม่มีหมอออกตรวจในวัน/สาขานี้'}
          />
        </Field>
      </div>

      {/* ── in-house only: spec the piece now, not after saving ─────────────────
          Ram, 2026-08-17. A piece made on the SSDC bench is designed by the counter at
          the moment it is ordered — the technician needs the pattern, colours and
          stickers to start. An outside lab is told what to make in `detail` and specced
          by them, so the builder would be an empty ceremony there. Same RetainerBuilder
          /lab uses; it hands its config back through onSave rather than writing, so the
          whole spec rides along on the INSERT instead of costing a second round trip. */}
      {/* `|| spec` matters: pick SSDC, spec the piece, then switch to an outside lab and
          the block would vanish while `save()` still sent the spec. State that is saved
          must stay on screen — so once a spec exists it is shown whatever the vendor. */}
      {creating && (inHouse || spec) && (
        <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-medium uppercase tracking-wide text-violet-500">ประเภทงาน & รีเทนเนอร์</span>
            {inHouse && <span className="text-[10.5px] text-violet-400">งาน in-house</span>}
          </div>
          {spec ? (
            <div className="rounded-lg bg-white border border-violet-200 px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[12.5px] font-medium text-slate-800 truncate">{spec.production_type || 'retainer'}</div>
                  {hasRetainer(spec.retainer) && (
                    <div className="text-[11.5px] text-slate-500 truncate">{retainerSummary(spec.retainer) || '—'}</div>
                  )}
                  {spec.retainer?.retainer_note && (
                    <div className="text-[11px] text-slate-400 truncate" title={spec.retainer.retainer_note}>{spec.retainer.retainer_note}</div>
                  )}
                </div>
                <button type="button" onClick={() => setSpecOpen(true)} disabled={busy}
                  className="inline-flex items-center gap-1 text-[11.5px] text-violet-500 hover:text-violet-700 shrink-0">
                  <Pencil size={11} /> แก้ไข
                </button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={() => setSpecOpen(true)} disabled={busy}
              className="w-full rounded-lg border border-dashed border-violet-300 px-3 py-2.5 text-[12.5px] font-medium text-violet-600 hover:bg-violet-100/60 disabled:opacity-40">
              ตั้งค่าประเภทงาน & รีเทนเนอร์
            </button>
          )}
          {/* Optional on purpose: an in-house order is sometimes raised before the
              design is agreed, and blocking the insert would push staff back to Bubble. */}
          {!spec && <p className="text-[11px] text-violet-400">ไม่ตั้งตอนนี้ก็ได้ — ตั้งทีหลังในหน้ารายละเอียดได้</p>}
        </div>
      )}

      {creating && (
        // ค่า Lab is always deferred — it has its own editor in the drawer that opens
        // right after saving, and an in-house piece has no vendor invoice yet anyway.
        <Field label="รายละเอียดงาน" hint={inHouse ? 'ค่า Lab ตั้งได้หลังบันทึก' : 'ประเภทงาน · รีเทนเนอร์ · ค่า Lab ตั้งได้หลังบันทึก'}>
          <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={3} disabled={busy}
            placeholder="เช่น RTN U/L · ฟันปลอมบน…" />
        </Field>
      )}

      {/* Renders over the create drawer (it is fixed inset-0 z-[60], the drawer is z-50).
          `onSave` captures instead of writing — there is no lab id to write to yet — and
          RetainerBuilder calls onClose() itself once onSave resolves. */}
      {specOpen && (
        <RetainerBuilder
          row={{ ...(spec || {}), patient_id: pid, _patient: { name: patientName, HN: patientHN } }}
          onSave={async (_r, next) => { setSpec(next) }}
          onClose={() => setSpecOpen(false)}
        />
      )}

      <InlineError>{err}</InlineError>
      {/* Embedded in the detail drawer there is exactly one บันทึก, and it is the
          drawer's. A second pair of buttons here is the thing this refactor removed. */}
      {!onFieldsChange && (
        <DrawerFooter
          onCancel={onCancel}
          onSave={save}
          saving={busy}
          canSave={canSave}
          saveLabel={creating ? 'เพิ่มงาน Lab' : 'บันทึก'}
          status={blockReason}
          statusTone="warn"
        />
      )}
    </div>
  )
}

function Dot() { return <span className="text-slate-300">·</span> }
function Doc({ img, code }) {
  return (
    <span className="inline-flex items-center gap-1 min-w-0">
      {img
        ? <img src={img} alt="" className="w-4 h-4 rounded-full object-cover bg-slate-100 shrink-0" />
        : <Stethoscope size={11} className="text-slate-400 shrink-0" />}
      <span className="text-slate-600 truncate max-w-[110px]">{code}</span>
    </span>
  )
}
// A neutral one-line note under a picker ("why is my appointment missing"). The amber
// warnings are kit <Banner tone="warn">; these grey hints are too quiet for a Banner.
function Hint({ icon: Icon, children }) {
  return (
    <p className="flex items-start gap-1.5 text-[12px] text-slate-500">
      {Icon && <Icon size={12} className="mt-0.5 shrink-0" />}<span>{children}</span>
    </p>
  )
}
